import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, Edit, Trash2 } from "lucide-react";
import { CustomerTags } from "./CustomerTags";
import { Customer } from "@/lib/types";
import EntityActions from "@/components/shared/EntityActions";
import CustomerBalanceDisplay from "./CustomerBalanceDisplay";
import { useCustomerBalances } from "@/hooks/useCustomerBalance";

interface CustomerListProps {
  customers: Customer[];
  viewCustomerDetails: (customer: Customer) => void;
  setEditing: (customer: Customer | null) => void;
  setOpen: (open: boolean) => void;
  handleDeleteCustomer: (customerId: string) => void;
}

export const CustomerList: React.FC<CustomerListProps> = ({
  customers,
  viewCustomerDetails,
  setEditing,
  setOpen,
  handleDeleteCustomer
}) => {
  // Fetch balances for all customers in one query
  const customerIds = customers.map(c => c.id);
  const { data: balanceMap } = useCustomerBalances(customerIds);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {customers.map((customer) => {
        const balance = balanceMap?.get(customer.id);
        
        return (
          <Card key={customer.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => viewCustomerDetails(customer)}>
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle>
                {customer.customer_type === 'commercial' && customer.business_name
                  ? customer.business_name
                  : `${customer.name} ${customer.last_name}`}
              </CardTitle>
              <EntityActions 
                size="sm"
                actions={[
                  {
                    label: "View",
                    icon: Eye,
                    onClick: () => viewCustomerDetails(customer),
                  },
                  {
                    label: "Edit",
                    icon: Edit,
                    onClick: () => { setEditing(customer); setOpen(true); },
                  },
                  {
                    label: "Delete",
                    icon: Trash2,
                    onClick: () => handleDeleteCustomer(customer.id),
                    destructive: true,
                    requiresConfirmation: {
                      title: "Delete Customer?",
                      description: "This action cannot be undone. This will permanently delete the customer and all associated data.",
                    },
                  },
                ]}
              />
            </CardHeader>
            <CardContent>
              <div className="mb-2">
                <span className="font-semibold">Phone(s): </span>
                {customer.cell_numbers && customer.cell_numbers.length > 0 ? customer.cell_numbers.join(", ") : "None provided"}
              </div>
              <div className="mb-2">
                <span className="font-semibold">Email(s): </span>
                {customer.emails && customer.emails.length > 0 ? customer.emails.join(", ") : "None provided"}
              </div>
              <div className="mb-2">
                <span className="font-semibold">Address: </span>
                {(() => {
                  const addressParts = [
                    customer.street_address,
                    customer.suburb,
                    customer.city,
                    customer.province_state,
                    customer.postal_code
                  ].filter(Boolean);
                  return addressParts.length > 0 ? addressParts.join(", ") : "None provided";
                })()}
              </div>
              
              {/* Customer Balance */}
              <div className="mb-2">
                <span className="font-semibold">Balance: </span>
                <CustomerBalanceDisplay balance={balance} variant="inline" />
              </div>
              
              <div className="mb-2">
                <span className="font-semibold">Lead Source: </span>
                {customer.lead_source || "None specified"}
                {customer.lead_source === "Other" && customer.lead_source_description && (
                  <span className="ml-1 text-sm text-muted-foreground">
                    ({customer.lead_source_description})
                  </span>
                )}
              </div>
              
              {/* Display tags if present */}
              {customer.tags && customer.tags.length > 0 && (
                <div className="mt-2">
                  <CustomerTags tags={customer.tags} />
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
